import React, { useEffect, useMemo, useState } from "react";
import HeaderContainer from "../../components/container/HeaderContainer";
import Backlink from "../../components/Backlink";
import { useUser } from "../../context/UserContext";
import { useMutation } from "@tanstack/react-query";
import CustomizedModal from "../../components/UI/CustomizedModal";
import { dataImport } from "../../services/AdminServices";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import DisplayTable from "../../components/tables/DisplayTable";
import { ImportFilesBaseStructure } from "../../components/tools/importFiles/ImportFilesBaseStructure";
import { ImportFilesConfirmation } from "../../components/tools/importFiles/ImportFilesConfirmation";
import { FaCheckCircle } from "react-icons/fa";
import { tableColumnWrap } from "../../components/tables/tableStyles";
const Sample_Results_File = "/SampleResultsImport_Template.csv";

export const ImportResults = () => {
  const { userInfo } = useUser();
  const { user_email } = userInfo;
  const [customModal, setCustomModal] = useState();
  const [inputFile, setInputFile] = useState({
    name: "",
  });
  const [formData, setFormData] = useState(null);

  const [response, setResponse] = useState({});

  const mutation = useMutation({
    mutationFn: dataImport,
    onError: (error) => {
      setCustomModal({
        title: "Error Occured",
        message: error?.response?.data?.error_details || error?.response?.data?.message,
      });
    },
  });

  useEffect(() => {
    if (mutation?.data?.data) {
      setResponse(mutation?.data?.data);
    }
  }, [mutation?.data?.data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "row_number",
        header: "row_number",
        size: 100
      },
      {
        accessorKey: "ps_code",
        header: "ps_code",
        size: 100
      },
      {
        accessorKey: "collection_date",
        header: "collection_date",
        size: 100
      },
      {
        accessorKey: "visit_id",
        header: "visit_id",
        size: 100
      },
      {
        accessorKey: "ftp_file_location",
        header: "ftp_file_location",
        size: 100
      },
      {
        accessorKey: "error",
        header: "error",
        size: 100,
        Cell: ({ cell }) => {
          const error = cell.getValue();
          if (error === null) return;
          return <div style={tableColumnWrap()}>{error}</div>;
        },
      }
    ],
    [response]
  );

  const insertResults = () => {
    setFormData(prev => {
      if (prev instanceof FormData) {
        const form = prev;
        form.append("import_type", "pfas_results");
        form.append("import_by", user_email);
        form.append("commit", true);
        return form;
      }
      return prev;
    });

    mutation.mutate(formData);
  };

  return (
    <>
      {customModal && (
        <CustomizedModal
          title={customModal.title}
          message={customModal.message}
          onCancel={() => setCustomModal(null)}
        />
      )}
      <section className="w-full text-primary">
        <Backlink>Back</Backlink>
        <section className="my-8 text-primary w-full">
          <HeaderContainer title="Results Import">
            <ImportFilesBaseStructure
              title={"Upload the Results CSV file to insert results."}
              sampleFileInfo={
                "File must be comma-separated, and first row must contain these fields: PWSID, PS_Code, Collection_Date, Analyte_Name, SWB_Result, Result_Units, Template_Eval, Water_System_Name, Facility_Name, SB_Lab_ID, FTP_File_Location, File_Track_ID."
              }
              sampleFilePath={Sample_Results_File}
              sampleFileText={"Results Import"}
              inputFile={inputFile}
              import_type={"pfas_results"}
              setInputFile={setInputFile}
              setFormData={setFormData}
              mutation={mutation}
            />

            {/* Inserted Rows */}
            {inputFile?.name &&
              response?.imported_records > 0 && (
                <>
                  <p className="mt-5 italic flex">
                    <FaCheckCircle className="text-green-500 w-[20px] h-[20px] mr-2" />
                    {response?.imported_records} of{" "}
                    {response?.total_records} records
                    inserted
                  </p>
                </>
              )
            }

            {/* Confirmation */}
            {inputFile?.name &&
              response?.valid_records > 0 &&
              !mutation.isError &&
              response?.imported_records === 0 && (
                <ImportFilesConfirmation
                  total_rows={response?.valid_records}
                  confirmation_text={
                    ` valid records out of ${response?.total_records} are ready to be inserted. Would you like to proceed with the insert?`
                  }
                  onConfirm={insertResults}
                  onCancel={() => {
                    setInputFile({ name: "" });
                    setResponse({});
                  }}
                />
              )
            }

            {mutation?.isPending ? (
              <LoadingSpinner size="30px" enableLoadingText={true} />
            ) : (
              <>

                {/* invalid Rows */}
                {inputFile.name && response?.invalid_records?.length > 0 && (
                  <>
                    <p className="mt-5 pb-2">
                      {response?.invalid_records.length} records found with errors
                    </p>
                    <DisplayTable
                      tableData={
                        response?.invalid_records
                      }
                      columns={columns}
                      showExportButton={true}
                    />
                  </>
                )}

              </>
            )}
          </HeaderContainer>
        </section>
      </section>
    </>
  );
};

export default ImportResults;